import type { PreviewTableRow } from "../../../_shared/_logic/table-contract"

const KEY_PART_SEPARATOR = "\u001f"
const KEY_ROW_SEPARATOR = "\u001e"
const KEY_LIST_SEPARATOR = "\u001d"

const keyPart = (value: unknown): string => String(value ?? "")

const tagKey = (
  tag:
    | {
        label?: string | null
        subLabel?: string | null
        metricLabel?: string | null
        metricAriaLabel?: string | null
        color?: string | null
        icon?: string | null
        tooltip?: string | null
        tooltipHtml?: boolean | null
      }
    | null
    | undefined,
): string =>
  [
    tag?.label,
    tag?.subLabel,
    tag?.metricLabel,
    tag?.metricAriaLabel,
    tag?.color,
    tag?.icon,
    tag?.tooltip,
    tag?.tooltipHtml ? "html" : "text",
  ]
    .map(keyPart)
    .join(KEY_PART_SEPARATOR)

const manualAppointmentKey = (
  appointment:
    | {
        start_at?: string | null
        end_at?: string | null
        employee_id?: string | null
        flex_type?: string | null
        timeframe_start_at?: string | null
        timeframe_end_at?: string | null
      }
    | null
    | undefined,
): string =>
  [
    appointment?.start_at,
    appointment?.end_at,
    appointment?.employee_id,
    appointment?.flex_type,
    appointment?.timeframe_start_at,
    appointment?.timeframe_end_at,
  ]
    .map(keyPart)
    .join(KEY_PART_SEPARATOR)

const manualFlexOptionsKey = (row: Exclude<PreviewTableRow, { isLoading: true }>): string => {
  const state = row.manualFlexSlotOptionsState

  if (state.status === "idle") return "idle"

  if (state.status === "loading")
    return ["loading", state.queryKey].map(keyPart).join(KEY_PART_SEPARATOR)

  if (state.status === "error") {
    return ["error", state.queryKey, state.message].map(keyPart).join(KEY_PART_SEPARATOR)
  }

  return [
    "ready",
    state.queryKey,
    state.options
      .map((option) =>
        [
          option.slot_id,
          option.label,
          option.employee_id,
          option.appointment_start_at,
          option.appointment_end_at,
          option.timeframe_start_at,
          option.timeframe_end_at,
        ]
          .map(keyPart)
          .join(KEY_PART_SEPARATOR),
      )
      .join(KEY_LIST_SEPARATOR),
  ]
    .map(keyPart)
    .join(KEY_PART_SEPARATOR)
}

const previewRowKey = (row: PreviewTableRow): string => {
  if ("isLoading" in row) return ["loading", row.id].map(keyPart).join(KEY_PART_SEPARATOR)

  return [
    row.id,
    row.propertyId,
    row.unitId,
    tagKey(row.propertyTag),
    row.appointmentStartAt,
    row.appointmentEndAt,
    row.appointmentTimeframeStartAt,
    row.appointmentTimeframeEndAt,
    row.appointmentSortValue,
    row.planningStrategy,
    row.appointmentTypeValue,
    row.appointmentTypeEditable ? "appointment-type-editable" : "",
    manualAppointmentKey(row.manualAppointment),
    row.manualFlexDate,
    row.manualFlexSlotId,
    manualFlexOptionsKey(row),
    tagKey(row.appointmentTag),
    tagKey(row.arrivalAnchor),
    tagKey(row.departureAnchor),
    tagKey(row.warningTag),
    row.searchText,
    row.committed ? "committed" : "",
    row.removable ? "removable" : "",
    row.removed ? "removed" : "",
    row.dirty ? "dirty" : "",
  ]
    .map(keyPart)
    .join(KEY_PART_SEPARATOR)
}

export const serializeAppointmentPlanningPreviewRowsForRefresh = (
  rows: PreviewTableRow[],
): string => rows.map(previewRowKey).join(KEY_ROW_SEPARATOR)
